/**
 * S16. Activity and errors.
 *
 * One page, two sections, because splitting them into tabs makes people check
 * only one. Errors come first, because they are the actionable half, and the
 * feed comes second, because it is the half that explains them.
 *
 * THE SINGLE BEST REASON TO OPEN THIS PAGE IS THE WIN. A console that only
 * ever lists what is broken teaches a person that opening it feels bad. So the
 * errors that stopped after the last deploy get their own sub-section, with a
 * check and a count, and they are the only thing on the page that is stated as
 * good news. The brief asks for it by name and it costs one collapsed strip.
 *
 * THE NO ERRORS STATE IS NOT A SHRUG. Herb and Ash has nothing wrong with it,
 * and the empty state says so as a statement, with a calm check, a sentence,
 * and the mono line that says when we last looked. It has no button, because a
 * button would imply there is something to do, and there is not.
 *
 * ONE GROUP OPEN AT A TIME, and that is a doctrine constraint rather than a
 * taste one: every open group carries an error block, and every error block
 * carries Copy fix request as a solid button. Two open groups would put two
 * ink blocks on one screen. An accordion keeps the rule, and it also keeps the
 * page scannable, which is what a person came here to do.
 *
 * THE FEED IS SENTENCES. No user agent strings, no addresses, no identifiers.
 * An audit log is a different product. Grouped by day with sticky headers, so
 * a long scroll never loses which day it is reading.
 *
 * SIMPLE VERSUS ADVANCED. Inside an open group, the request that hit it and
 * the stack with the person's own frames marked sit behind one disclosure: one
 * click away in Simple, already open in Advanced, and printed exactly once
 * either way. Advanced also appends a filter bar (time range, path prefix)
 * BELOW the groups, so turning the switch on never pushes an error down the
 * page. The grouping, the counts and the fix request are identical in both
 * modes. The request volume chart the
 * spec also lists is not built: it needs per hour series nothing else in the
 * prototype has, and the number it would draw is on the usage screen already.
 *
 * FAKEABLE STATES, deep linkable per prototype mechanic (a):
 *   ?state=no-errors   the reassurance state, on any project
 *   ?state=open        the first group opened, for the expanded screenshot
 *   ?state=empty       a project nothing has happened to yet
 *   ?state=loading     the skeletons, held open
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router';

import { Select } from '../../components/primitives/Select';
import { Field } from '../../components/primitives/Field';

import { ActivityList, ActivityRow } from '../../components/patterns/ActivityRow';
import { AdvancedDisclosure } from '../../components/patterns/AdvancedDisclosure';
import { dateLabel } from '../../components/patterns/DeployRow';
import { EmptyState } from '../../components/patterns/EmptyState';
import { ErrorBlock } from '../../components/patterns/ErrorBlock';
import { ErrorGroupList, ErrorGroupRow, ResolvedGroupRow } from '../../components/patterns/ErrorGroupRow';
import { Notice } from '../../components/patterns/Notice';
import { SectionPanel } from '../../components/patterns/SectionPanel';
import { Skeleton } from '../../components/patterns/Skeleton';

import { environmentOf, getProject, listActivity, listDeploys, listErrors } from '../../lib/api';
import { useProjects } from '../../lib/auth/use-app-data';
import { count } from '../../lib/format';
import { useAdvanced } from '../../lib/hooks/use-advanced';
import { useAsync } from '../../lib/hooks/use-async';
import { useProjectSlug } from '../../lib/hooks/use-project-slug';
import { useScreenState } from '../../lib/hooks/use-screen-state';
import { t } from '../../lib/i18n/t';
import { errorDetail, errorFixRequest } from '../../lib/mock/seed';
import { useDemoStore } from '../../lib/mock/store';
import { captureEvent } from '../../lib/analytics/posthog';
import type { ActivityEvent, ErrorGroup } from '../../lib/types';

import '../../styles/components/activity.css';
// The ADVANCED chip is one treatment in one place. This screen borrows it
// from the disclosure rather than writing a second token rule for it.
import '../../styles/components/disclosure.css';

type StateName = 'resting' | 'no-errors' | 'open' | 'empty' | 'loading';
const STATES: StateName[] = ['resting', 'no-errors', 'open', 'empty', 'loading'];

const DAY = 24 * 60 * 60 * 1000;

/** The choices the Advanced filter offers. 7 days is the simple default,
 *  because "No errors in the last 7 days" is the sentence the empty state
 *  promises and a filter that quietly disagreed with it would be a bug. */
const WINDOWS = [1, 7, 30];

/* ------------------------------------------------------------- day grouping */

function dayKey(iso: string): string {
  const at = new Date(iso);
  return `${at.getFullYear()}-${at.getMonth()}-${at.getDate()}`;
}

/** Today, Yesterday, then an absolute date. Never a relative day beyond that. */
function dayLabel(iso: string, now: number): string {
  const key = dayKey(iso);
  if (key === dayKey(new Date(now).toISOString())) return t('Today');
  if (key === dayKey(new Date(now - DAY).toISOString())) return t('Yesterday');
  return dateLabel(iso, now);
}

interface DayBucket {
  key: string;
  label: string;
  items: ActivityEvent[];
}

function byDay(events: ActivityEvent[], now: number): DayBucket[] {
  const buckets = new Map<string, ActivityEvent[]>();
  for (const item of events) {
    const day = dayLabel(item.at, now);
    const existing = buckets.get(day);
    if (existing) existing.push(item);
    else buckets.set(day, [item]);
  }
  const days: DayBucket[] = [];
  for (const [label, items] of buckets.entries()) {
    days.push({ key: label, label, items });
  }
  return days;
}

function withinDays(iso: string, days: number, now: number): boolean {
  return now - new Date(iso).getTime() <= days * 24 * 60 * 60 * 1000;
}

/* ------------------------------------------------------- the resolved strip */

function ResolvedSection({ groups }: { groups: ErrorGroup[] }) {
  const [open, setOpen] = useState(false);
  const occurrences = groups.reduce((sum, group) => sum + group.count, 0);

  return (
    <section className="won" aria-label={t('Errors that stopped')}>
      <button
        type="button"
        className="won__head"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        <span className="won__check" aria-hidden="true">
          <svg viewBox="0 0 16 16" width="13" height="13" focusable="false">
            <path
              d="M3 8.5 6.5 12 13 4.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span className="won__say">
          {occurrences === 1
            ? t('1 error stopped after your last deploy.')
            : t('{n} errors stopped after your last deploy.', { n: count(occurrences) })}
        </span>
        <span className="won__more">{open ? t('Hide') : t('Show')}</span>
      </button>

      {open ? (
        <div className="won__body">
          <ErrorGroupList label={t('Errors that stopped')}>
            {groups.map((group) => (
              <ResolvedGroupRow group={group} key={group.id} />
            ))}
          </ErrorGroupList>
        </div>
      ) : null}
    </section>
  );
}

/* ------------------------------------------------------------------ screen */

export default function Activity() {
  const slug = useProjectSlug() ?? '';
  const scenarioId = useDemoStore((s) => s.scenarioId);
  const { state } = useScreenState<StateName>(STATES, 'resting');
  const [advanced] = useAdvanced();

  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [windowDays, setWindowDays] = useState(7);
  const [prefix, setPrefix] = useState('');

  const cachedProjects = useProjects();
  const cachedProject = slug ? cachedProjects.find((p) => p.slug === slug) : undefined;
  const { data: fetchedProject, isLoading: isProjectLoading } = useAsync(
    () => (slug ? getProject(slug) : Promise.resolve(null)),
    [slug, scenarioId],
  );
  const project = fetchedProject ?? cachedProject;
  const projectId = project?.id;

  const { data: errors } = useAsync(
    () => (projectId ? listErrors(projectId) : Promise.resolve(undefined)),
    [projectId, scenarioId],
  );
  const { data: activity } = useAsync(
    () => (projectId ? listActivity(projectId) : Promise.resolve(undefined)),
    [projectId, scenarioId],
  );
  const { data: deploys } = useAsync(
    () => (projectId ? listDeploys(projectId) : Promise.resolve(undefined)),
    [projectId, scenarioId],
  );

  useEffect(() => {
    if (project && errors) {
      const openCount = errors.filter((g) => g.state !== 'resolved').length;
      const resolvedCount = errors.filter((g) => g.state === 'resolved').length;
      captureEvent('activity_screen_viewed', {
        project_slug: project.slug,
        open_errors_count: openCount,
        resolved_errors_count: resolvedCount,
      });
    }
  }, [project, errors]);

  if ((isProjectLoading && !project) || state === 'loading') {
    return (
      <div className="page">
        <header className="page__head">
          <h1 className="page__title">{t('Activity')}</h1>
        </header>
        <Skeleton shape="row" count={3} label={t('Looking for errors')} />
        <Skeleton shape="row" count={6} label={t('Loading what happened')} />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="page">
        <header className="page__head">
          <h1 className="page__title">{t('Activity')}</h1>
        </header>
        <Notice tone="quiet" title={t('That project is not here.')}>
          <p>{t('It may have been deleted, or the address may have a typo in it.')}</p>
        </Notice>
        <p>
          <Link className="link-inline" to="/projects">
            {t('All projects')}
          </Link>
        </p>
      </div>
    );
  }

  const base = `/p/${encodeURIComponent(project.slug)}`;
  const now = Date.now();

  const currentDeployId = environmentOf(project).currentDeployId;
  const currentDeploy = deploys?.find((d) => d.id === currentDeployId || d.isCurrent);
  const currentHash = currentDeploy?.commitSha;
  const currentCreated = currentDeploy ? new Date(currentDeploy.createdAt).getTime() : 0;

  const hideErrors = state === 'no-errors';
  const hideActivity = state === 'empty';

  const isCurrentDeployError = (group: ErrorGroup): boolean => {
    if (!currentDeploy) return true;
    if (group.deployHash && currentHash) {
      if (group.deployHash === currentHash) return true;
      if (currentHash.startsWith(group.deployHash) || group.deployHash.startsWith(currentHash)) return true;
    }
    if (currentCreated > 0 && new Date(group.lastSeen).getTime() >= currentCreated) {
      return true;
    }
    if (group.deployHash && currentHash && group.deployHash !== currentHash) {
      return false;
    }
    return true;
  };

  const currentErrors = (errors ?? []).filter(isCurrentDeployError);
  const resolved = (errors ?? []).filter((group) => group.state === 'resolved');

  const open = hideErrors
    ? []
    : currentErrors.filter((group) => {
        if (group.state === 'resolved') return false;
        if (advanced) {
          if (!withinDays(group.lastSeen, windowDays, now)) return false;
          if (prefix.trim() !== '' && !group.location.toLowerCase().includes(prefix.trim().toLowerCase())) {
            return false;
          }
        }
        return true;
      });

  const openId = state === 'open' && open.length > 0 ? (openGroup ?? open[0].id) : openGroup;
  const days = hideActivity || !activity ? [] : byDay(activity, now);

  const openGroups = hideErrors ? [] : currentErrors.filter((group) => group.state !== 'resolved');
  const filtered = open.length === 0 && openGroups.length > 0;

  return (
    <div className="page">
      <header className="page__head">
        <h1 className="page__title">{t('Activity')}</h1>
      </header>

      <SectionPanel title={t('Errors')} flush>
        {errors === undefined ? (
          <div className="ov__strip" style={{ paddingBottom: 'var(--space-4)' }}>
            <Skeleton shape="row" count={3} label={t('Looking for errors')} />
          </div>
        ) : open.length === 0 ? (
          <div className="act__calm">
            <EmptyState
              mark={filtered ? 'box' : 'clear'}
              sentence={
                filtered
                  ? t('Nothing matches what you picked.')
                  : windowDays === 1
                    ? t('No errors in the last 24 hours.')
                    : t('No errors in the last {n} days.', { n: windowDays })
              }
              context={
                filtered
                  ? t('Change the time range or the path below to see the rest.')
                  : undefined
              }
            />
            <p className="act__checked mono">{t('Last checked just now')}</p>
          </div>
        ) : (
          <ErrorGroupList label={t('Errors your app is returning')}>
            {open.map((group) => {
              const detail = errorDetail(group.id);
              return (
                <ErrorGroupRow
                  group={group}
                  key={group.id}
                  open={openId === group.id}
                  onToggle={() => setOpenGroup(openId === group.id ? null : group.id)}
                >
                  <ErrorBlock
                    summary={t(group.summary)}
                    cause={t(detail?.cause ?? group.raw)}
                    location={group.location}
                    fixRequest={errorFixRequest(group, project.name)}
                  />

                  {detail ? (
                    <div className="act__adv">
                      <AdvancedDisclosure label={t('Show the stack trace')}>
                        <p className="act__adv-say">
                          {t('The request that hit it, and where it came from in your code.')}
                        </p>

                        <p className="act__req mono">
                          {detail.request.method} {detail.request.path}{' '}
                          <span className="act__req-status">{detail.request.status}</span>
                        </p>

                        <ol className="act__stack mono">
                          {detail.stack.split('\n').map((frame, index) => (
                            <li
                              className="act__frame"
                              data-own={
                                detail.ownFrames.some((own) => frame.includes(own)) ? 'yes' : 'no'
                              }
                              key={`${frame}-${index}`}
                            >
                              {frame}
                            </li>
                          ))}
                        </ol>
                      </AdvancedDisclosure>
                    </div>
                  ) : null}
                </ErrorGroupRow>
              );
            })}
          </ErrorGroupList>
        )}

        {errors !== undefined && advanced && openGroups.length > 0 ? (
          <div className="act__filters">
            <span className="disc__chip">{t('ADVANCED')}</span>
            <Select
              label={t('Time range')}
              value={String(windowDays)}
              onChange={(event) => setWindowDays(Number(event.target.value))}
              options={WINDOWS.map((days_) => ({
                value: String(days_),
                label:
                  days_ === 1
                    ? t('Last 24 hours')
                    : t('Last {n} days', { n: count(days_) }),
              }))}
            />
            <Field
              label={t('Route prefix')}
              placeholder="/api"
              value={prefix}
              onChange={(event) => setPrefix(event.target.value)}
            />
          </div>
        ) : null}

        {errors !== undefined && !hideErrors && resolved.length > 0 ? (
          <div className="act__won">
            <ResolvedSection groups={resolved} />
          </div>
        ) : null}
      </SectionPanel>

      <SectionPanel title={t('Activity')} flush>
        {activity === undefined ? (
          <div className="ov__strip" style={{ paddingBottom: 'var(--space-4)' }}>
            <Skeleton shape="row" count={6} label={t('Loading what happened')} />
          </div>
        ) : days.length === 0 ? (
          <EmptyState
            mark="clock"
            sentence={t('Nothing has happened yet. Your first deploy will show up here.')}
          />
        ) : (
          <div className="act__feed">
            {days.map((bucket) => (
              <section className="day" key={bucket.key} aria-label={bucket.label}>
                <header className="day__head">
                  <h3 className="day__title">{bucket.label}</h3>
                </header>
                <ActivityList label={bucket.label}>
                  {bucket.items.map((item) => (
                    <ActivityRow event={item} key={item.id} />
                  ))}
                </ActivityList>
              </section>
            ))}
          </div>
        )}
      </SectionPanel>

      {advanced ? (
        <p className="act__logs">
          <Link className="link-inline" to={`${base}/logs`}>
            {t('Watch the live logs')}
          </Link>
        </p>
      ) : null}
    </div>
  );
}
